/**
 * Regelversion 12: Gesetzesevaluierung.
 *
 * Anderthalb Jahre nachdem ein eigenes Gesetz seine volle Wirkung entfaltet hat, wird es evaluiert:
 *  - Erfolg: Es wirkt besser als erwartet. Das Problem entspannt sich weiter, die Begünstigten merken es.
 *  - Nebenwirkung: Mehrkosten, Bürokratie für Betriebe, Mitnahmeeffekte oder Umgehung. Sie bleibt, bis ein
 *    neues Gesetz zum selben Thema in Kraft tritt (Nachbesserung, Novelle oder Aufbaugesetz).
 *  - Schwach: Es bleibt hinter den Erwartungen zurück. Das Problem wächst etwas, nachbessern lohnt sich.
 * Wie es ausgeht, hängt von der Qualität der Umsetzung (Ministerium, Verwaltung) und davon ab, ob das
 * Problem kleiner geworden ist. Nur im Einzelspieler, nur für eigene Gesetze.
 */
import { S, log } from './state';
import { since } from './version';
import { rnd } from './rng';
import { clamp, de } from './util';
import { OUTLETS } from '../content/parties';
import { press } from './media';
import { ISS } from '../content/states';

export const evalOn = (): boolean => since(12) && !S.mp;
export const EVAL_AFTER = 18;
export type EvalRes = { lid: number; t: string; iss: string; m: number; k: 'erfolg' | 'neben' | 'schwach'; side?: string; c?: number; fixed?: number | null };
const A = () => S as any;
export const evals = (): EvalRes[] => (A().evals ??= []);
export const openFix = (): EvalRes[] => (A().evals || []).filter((e: EvalRes) => e.k !== 'erfolg' && e.fixed == null);

const SIDE: Record<string, [string, string]> = {
  kosten: ['Mehrkosten', 'Die Kosten liegen deutlich über der Planung.'],
  buero: ['Bürokratie', 'Betriebe klagen über Formulare und Nachweispflichten.'],
  mitnahme: ['Mitnahmeeffekte', 'Das Geld landet oft bei denen, die es nicht gebraucht hätten.'],
  umgehung: ['Umgehung', 'Findige Akteure umgehen die Regeln, die Wirkung verpufft teilweise.'],
};
export const sideName = (k?: string) => (k ? SIDE[k]?.[0] : '') || '';

function evaluate(L: any) {
  const st = S.issues[L.iss], sev = st?.on ? st.sev : 0;
  const q = L.q || 1, fx = L.fx || {};
  const pOk = clamp(.38 + (q - 1) * 1.2 + (sev < 40 ? .15 : sev > 65 ? -.12 : 0), .1, .7);
  const r = rnd(); const E: EvalRes = { lid: L.id, t: L.t, iss: L.iss, m: S.m, k: r < pOk ? 'erfolg' : r < pOk + (1 - pOk) * .5 ? 'neben' : 'schwach', fixed: null };
  L.ev = E.k;
  if (E.k === 'erfolg') {
    if (st) st.sev = clamp(st.sev - 4, 0, 100);
    for (const g in fx.g || {}) if (fx.g[g] > 0) S.bonus[g] += Math.min(1.5, fx.g[g] * .25);
    log(`Evaluierung: „${L.t}“ wirkt besser als erwartet.`, 'good'); press(OUTLETS[1].n, `Studie: „${L.t}“ ist ein Erfolg`, .8);
  } else if (E.k === 'neben') {
    const k = (fx.b || 0) < -2 ? 'kosten' : (fx.g?.unt || 0) < -.5 ? 'buero' : (fx.gini || 0) < 0 || Object.values<number>(fx.g || {}).filter((v) => v > 0).length >= 3 ? 'mitnahme' : 'umgehung';
    E.side = k;
    if (k === 'kosten') { E.c = Math.round(clamp(Math.abs(fx.b || 0) * .25, .5, 4) * 10) / 10; S.lawB -= E.c; }
    if (k === 'mitnahme') S.giniLaw = (S.giniLaw || 0) + .15;
    if (k === 'umgehung' && st) { st.sev = clamp(st.sev + 4, 0, 100); st.cut = (st.cut || 0) * .85; }
    log(`Evaluierung: „${L.t}“ hat eine Nebenwirkung. ${SIDE[k][0]}: ${SIDE[k][1]}${E.c ? ` Mehrkosten ${de(E.c, 1)} Mrd € im Jahr.` : ''} Ein neues Gesetz zum Thema kann nachbessern.`, 'bad');
    press(OUTLETS[1].n, `Evaluierung: ${SIDE[k][0]} bei „${L.t}“`, -.6);
  } else {
    if (st) { st.sev = clamp(st.sev + 3, 0, 100); if (!st.on && st.sev >= 15) { st.on = true; st.solved = null; } }
    log(`Evaluierung: „${L.t}“ bleibt hinter den Erwartungen zurück. Nachbessern würde sich lohnen.`, 'bad');
    press(OUTLETS[1].n, `Studie: „${L.t}“ verfehlt seine Ziele`, -.5);
  }
  evals().push(E);
}

/** Monatlich in der Regierungszeit. Höchstens eine Evaluierung pro Monat. */
export function evalTick(): boolean {
  if (!evalOn() || S.phase !== 'govern') return false;
  for (const E of openFix()) if (E.side === 'buero') S.bonus.unt -= .03; // Bürokratie belastet Betriebe, bis nachgebessert ist
  const L = (S.laws || []).find((x: any) => x.stage === 'done' && !x.ev && !x.rep && !x.bestand && (!x.by || x.by === 'YOU') && x.doneM != null && S.m - x.doneM >= EVAL_AFTER && x.iss && ISS[x.iss]);
  if (!L) return false;
  evaluate(L); return true;
}

/** Ein neues Gesetz zum selben Thema tritt in Kraft: Nebenwirkungen und Schwächen sind behoben. Aufruf aus enact(). */
export function evalFixed(L: any) {
  if (!evalOn() || L.rep) return;
  for (const E of openFix()) if (E.iss === L.iss && E.lid !== L.id) {
    E.fixed = S.m;
    if (E.side === 'kosten' && E.c) S.lawB += E.c;
    if (E.side === 'mitnahme') S.giniLaw = (S.giniLaw || 0) - .15;
    log(`Nachbesserung: Mit „${L.t}“ ist die Schwäche von „${E.t}“ behoben.`, 'good');
  }
}
